/* ═══════════════════════════════════════════════════════════════════
   COREVIAN UNIVERSITY — BIBLE TOTP ENROLLMENT INTERFACE (REVISED)
   ───────────────────────────────────────────────────────────────────
   CEO-only. UI visibility here is convenience only — the actual
   authorization boundary is enforced entirely server-side, inside
   enroll-bible-totp / request-bible-challenge / verify-bible-challenge.
   Nothing in this file, including the CEO-UUID check below, is a
   security control on its own.

   REVISION — three corrections from review:
   1. The credential-exposed state can no longer be dismissed by a
      backdrop click. It was previously the only accidental-dismissal
      path; it is now blocked specifically during that state.
   2. Completion now requires BOTH recovery-code confirmation AND a
      successful server-side TOTP verification against the newly
      installed secret, proving the authenticator actually received
      it — not just that the database write succeeded. This chains
      the existing, already-reviewed request-bible-challenge and
      verify-bible-challenge calls; no server-side change was made or
      needed (see the reasoning delivered alongside this file).
   3. Wording no longer claims JavaScript guarantees immediate
      destruction of secret material from process memory. It
      accurately describes removing rendered DOM content and
      releasing application-level references where practical — never
      a guarantee about the underlying bytes.

   This file still does NOT:
     - alter enroll-bible-totp, request-bible-challenge,
       verify-bible-challenge, any RPC, or any cryptographic format
     - persist the TOTP secret, otpauth URI, or recovery codes anywhere
       (no localStorage, no sessionStorage, no database write)
     - log secret material to the console under any circumstance
     - call any privileged database table directly

   Requires: a QR-rendering library loaded before this file, exposing
   a global `QRCode` constructor.
   ═══════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  const BIBLE_ENROLL = {
    authorizedUserId: 'e61d0c96-b6d1-4c09-bead-51c0103044a2',
    supabaseUrl: 'https://xoolcbbpnvdylcoorlqg.supabase.co',
    publishableKey: 'sb_publishable_bjOrV5qp2n0PtuVFOSA_6g_3Micws2T'
  };

  let overlayInstalled = false;

  // True only while the success/credential-display state is
  // rendered. Checked by the overlay's backdrop-click handler so
  // that state specifically cannot be dismissed that way.
  let credentialsCurrentlyDisplayed = false;

  /* ============================================================
     DEMO MODE / AUTH HELPERS
     ============================================================ */

  function isDemoMode() {
    if (window.demoMode === true) return true;
    const banner = document.getElementById('demoBanner');
    if (!banner) return false;
    return window.getComputedStyle(banner).display !== 'none';
  }

  function getUniversityClient() {
    try {
      if (typeof window.getCorevianSupabaseClient !== 'function') {
        console.error('[Bible Enrollment] University Supabase client is not available.');
        return null;
      }
      return window.getCorevianSupabaseClient();
    } catch (error) {
      console.error('[Bible Enrollment] client error:', error);
      return null;
    }
  }

  async function getAuthSession() {
    const client = getUniversityClient();
    if (!client) return null;
    const { data, error } = await client.auth.getSession();
    if (error) throw error;
    return data?.session || null;
  }

  function isAuthorizedCEO(session) {
    return session?.user?.id === BIBLE_ENROLL.authorizedUserId;
  }

  function escapeHTML(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /* ============================================================
     EDGE FUNCTION CALLERS
     ============================================================ */

  async function callFunction(name, payload) {
    const session = await getAuthSession();
    if (!session || !session.access_token) {
      throw new Error('Authentication session not available.');
    }

    const response = await fetch(`${BIBLE_ENROLL.supabaseUrl}/functions/v1/${name}`, {
      method: 'POST',
      headers: {
        apikey: BIBLE_ENROLL.publishableKey,
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload || {})
    });

    const text = await response.text();
    let body;
    try { body = text ? JSON.parse(text) : null; } catch { body = null; }

    return { ok: response.ok, status: response.status, body };
  }

  const callEnroll = (bibleSessionToken) =>
    callFunction('enroll-bible-totp', bibleSessionToken ? { bibleSessionToken } : {});

  const callRequestChallenge = () => callFunction('request-bible-challenge', {});

  const callVerifyChallenge = (attemptId, code) =>
    callFunction('verify-bible-challenge', { attemptId, code });

  /* ============================================================
     STYLES
     ============================================================ */

  function installStyles() {
    if (document.getElementById('cvn-bible-enroll-styles')) return;
    const style = document.createElement('style');
    style.id = 'cvn-bible-enroll-styles';
    style.textContent = `
      #cvnBibleEnrollOverlay {
        position: fixed; inset: 0; z-index: 13000; display: none;
        background: rgba(4,12,25,.90); backdrop-filter: blur(6px);
        overflow-y: auto; padding: 30px; box-sizing: border-box;
      }
      #cvnBibleEnrollOverlay.cvn-open { display: block; }
      .cvn-be-shell {
        width: min(620px, 100%); margin: 0 auto; border-radius: 16px;
        overflow: hidden; background: #0B1728; border: 1px solid rgba(94,212,207,0.20);
        box-shadow: 0 32px 90px rgba(0,0,0,.5);
      }
      .cvn-be-header {
        padding: 26px 30px; background: linear-gradient(135deg, #0B1426, #10263B);
        border-bottom: 1px solid rgba(94,212,207,0.16);
      }
      .cvn-be-eyebrow { color: #d6ad63; font-size: 10px; font-weight: 800; letter-spacing: 1.6px; text-transform: uppercase; margin-bottom: 6px; }
      .cvn-be-title { color: #fff; font-size: 20px; font-weight: 800; margin: 0; }
      .cvn-be-body { padding: 26px 30px 30px; color: #b8c8d8; font-size: 13px; line-height: 1.6; }
      .cvn-be-loading, .cvn-be-error { text-align: center; padding: 30px 10px; }
      .cvn-be-error { color: #fca5a5; }
      .cvn-be-field { margin: 18px 0; }
      .cvn-be-label { display: block; font-size: 11px; font-weight: 800; color: #8193a6; text-transform: uppercase; letter-spacing: .8px; margin-bottom: 8px; }
      .cvn-be-input {
        width: 100%; box-sizing: border-box; padding: 12px 14px; border-radius: 8px;
        border: 1px solid rgba(94,212,207,0.30); background: rgba(255,255,255,.04);
        color: #fff; font-size: 16px; letter-spacing: 2px; text-align: center; font-family: monospace;
      }
      .cvn-be-btn {
        display: inline-flex; align-items: center; justify-content: center;
        padding: 11px 20px; border-radius: 8px; font-weight: 800; font-size: 13px;
        cursor: pointer; border: 1px solid transparent;
      }
      .cvn-be-btn:disabled { opacity: .45; cursor: not-allowed; }
      .cvn-be-btn-primary { background: #19B5B0; color: #071526; }
      .cvn-be-btn-secondary { background: transparent; border-color: rgba(94,212,207,0.35); color: #59D3CE; }
      .cvn-be-qr-wrap { display: flex; justify-content: center; padding: 16px; background: #fff; border-radius: 12px; margin: 16px 0; }
      .cvn-be-manual-key {
        font-family: monospace; font-size: 14px; letter-spacing: 1px; word-break: break-all;
        background: rgba(255,255,255,.04); border: 1px solid rgba(94,212,207,0.2); border-radius: 8px;
        padding: 12px 14px; color: #fff; margin-bottom: 8px;
      }
      .cvn-be-codes-grid {
        display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin: 14px 0;
        font-family: monospace; font-size: 14px;
      }
      .cvn-be-code {
        background: rgba(255,255,255,.04); border: 1px solid rgba(94,212,207,0.2);
        border-radius: 6px; padding: 9px 10px; color: #fff; text-align: center; letter-spacing: 1px;
      }
      .cvn-be-confirm-row { display: flex; align-items: flex-start; gap: 10px; margin: 20px 0; }
      .cvn-be-confirm-row input { margin-top: 3px; }
      .cvn-be-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 18px; }
      .cvn-be-warning {
        background: rgba(251,191,36,.08); border: 1px solid rgba(251,191,36,.3);
        border-radius: 8px; padding: 12px 14px; color: #fbbf24; font-size: 12px; margin-bottom: 16px;
      }
      .cvn-be-verify-row { display: flex; gap: 10px; align-items: center; }
      .cvn-be-verify-row .cvn-be-input { text-align: left; letter-spacing: 3px; }
      .cvn-be-verify-status { font-size: 12px; margin-top: 8px; min-height: 16px; }
      .cvn-be-verify-status.ok { color: #4ADE80; }
      .cvn-be-verify-status.fail { color: #fca5a5; }
      .cvn-be-checklist-item { display: flex; align-items: center; gap: 8px; font-size: 12px; color: #8193a6; margin-top: 6px; }
      .cvn-be-checklist-item.done { color: #4ADE80; }
    `;
    document.head.appendChild(style);
  }

  /* ============================================================
     RENDER STATES
     ============================================================ */

  function panelEl() { return document.getElementById('cvnBibleEnrollPanel'); }

  function renderLoading(message) {
    const el = panelEl();
    if (el) el.innerHTML = `<div class="cvn-be-loading">${escapeHTML(message || 'Working…')}</div>`;
  }

  function renderError(message) {
    const el = panelEl();
    if (el) {
      el.innerHTML = `
        <div class="cvn-be-error">${escapeHTML(message)}</div>
        <div class="cvn-be-actions">
          <button type="button" class="cvn-be-btn cvn-be-btn-secondary" id="cvnBibleEnrollClose2">Close</button>
        </div>
      `;
      document.getElementById('cvnBibleEnrollClose2')?.addEventListener('click', closeEnrollmentPanel);
    }
  }

  function renderChallengeForm(onSubmit) {
    const el = panelEl();
    if (!el) return;
    el.innerHTML = `
      <p>An authenticator is already configured for this account. Enter your current 6-digit code, or one of your recovery codes, to continue.</p>
      <div class="cvn-be-field">
        <label class="cvn-be-label" for="cvnBibleChallengeInput">Current code</label>
        <input class="cvn-be-input" id="cvnBibleChallengeInput" type="text" autocomplete="one-time-code" placeholder="000000" maxlength="16">
      </div>
      <div class="cvn-be-actions">
        <button type="button" class="cvn-be-btn cvn-be-btn-secondary" id="cvnBibleChallengeCancel">Cancel</button>
        <button type="button" class="cvn-be-btn cvn-be-btn-primary" id="cvnBibleChallengeSubmit">Continue</button>
      </div>
    `;
    document.getElementById('cvnBibleChallengeCancel')?.addEventListener('click', closeEnrollmentPanel);
    document.getElementById('cvnBibleChallengeSubmit')?.addEventListener('click', () => {
      const value = document.getElementById('cvnBibleChallengeInput')?.value?.trim();
      if (!value) return;
      onSubmit(value);
    });
  }

  function renderSuccess(result) {
    const el = panelEl();
    if (!el) return;

    // This state exposes the secret and recovery codes — the overlay
    // is no longer dismissible by a backdrop click while this flag
    // is true. See installOverlay()'s click handler.
    credentialsCurrentlyDisplayed = true;

    // Local-only state, held in this closure alone — never written
    // to window, localStorage, sessionStorage, or any database call.
    // Completion requires BOTH of these to become true.
    let recoveryCodesConfirmed = false;
    let totpVerified = false;
    let liveAttemptId = null;

    el.innerHTML = `
      <div class="cvn-be-warning">
        ${result.reEnrollment
          ? 'This replaces the current authenticator. The previous device and its unused recovery codes are now invalid.'
          : 'This is the only time these values will ever be shown. Save your recovery codes securely, and confirm your authenticator below, before this window can be closed.'}
      </div>

      <div class="cvn-be-field">
        <label class="cvn-be-label">Scan with your authenticator app</label>
        <div class="cvn-be-qr-wrap" id="cvnBibleQrTarget"></div>
      </div>

      <div class="cvn-be-field">
        <label class="cvn-be-label">Or enter manually</label>
        <div class="cvn-be-manual-key" id="cvnBibleManualKey">${escapeHTML(result.base32Secret)}</div>
        <button type="button" class="cvn-be-btn cvn-be-btn-secondary" id="cvnCopyManualKey">Copy key</button>
      </div>

      <div class="cvn-be-field">
        <label class="cvn-be-label">Confirm your authenticator is working</label>
        <div class="cvn-be-verify-row">
          <input class="cvn-be-input" id="cvnBibleVerifyCodeInput" type="text" autocomplete="one-time-code" placeholder="000000" maxlength="6">
          <button type="button" class="cvn-be-btn cvn-be-btn-secondary" id="cvnBibleVerifyCodeBtn">Verify</button>
        </div>
        <div class="cvn-be-verify-status" id="cvnBibleVerifyStatus"></div>
      </div>

      <div class="cvn-be-field">
        <label class="cvn-be-label">Recovery codes — save these now, in a secure location</label>
        <div class="cvn-be-codes-grid" id="cvnBibleCodesGrid">
          ${result.recoveryCodes.map((c) => `<div class="cvn-be-code">${escapeHTML(c)}</div>`).join('')}
        </div>
        <button type="button" class="cvn-be-btn cvn-be-btn-secondary" id="cvnCopyAllCodes">Copy all codes</button>
      </div>

      <div class="cvn-be-confirm-row">
        <input type="checkbox" id="cvnBibleConfirmSaved">
        <label for="cvnBibleConfirmSaved">I have saved my recovery codes in a secure location.</label>
      </div>

      <div class="cvn-be-checklist-item" id="cvnBibleChecklistCodes">○ Recovery codes confirmed saved</div>
      <div class="cvn-be-checklist-item" id="cvnBibleChecklistTotp">○ Authenticator verified</div>

      <div class="cvn-be-actions">
        <button type="button" class="cvn-be-btn cvn-be-btn-primary" id="cvnBibleEnrollDone" disabled>Done</button>
      </div>
    `;

    if (typeof window.QRCode === 'function') {
      new window.QRCode(document.getElementById('cvnBibleQrTarget'), {
        text: result.otpauthUri,
        width: 220,
        height: 220
      });
    } else {
      document.getElementById('cvnBibleQrTarget').innerHTML =
        '<div style="color:#9C3838;padding:20px;">QR library not loaded — use the manual key below.</div>';
    }

    document.getElementById('cvnCopyManualKey')?.addEventListener('click', () => {
      navigator.clipboard?.writeText(result.base32Secret);
    });

    document.getElementById('cvnCopyAllCodes')?.addEventListener('click', () => {
      navigator.clipboard?.writeText(result.recoveryCodes.join('\n'));
    });

    const doneButton = document.getElementById('cvnBibleEnrollDone');
    const checklistCodes = document.getElementById('cvnBibleChecklistCodes');
    const checklistTotp = document.getElementById('cvnBibleChecklistTotp');

    function refreshDoneState() {
      if (doneButton) doneButton.disabled = !(recoveryCodesConfirmed && totpVerified);
    }

    document.getElementById('cvnBibleConfirmSaved')?.addEventListener('change', (event) => {
      recoveryCodesConfirmed = event.target.checked;
      checklistCodes.textContent = (recoveryCodesConfirmed ? '✓' : '○') + ' Recovery codes confirmed saved';
      checklistCodes.classList.toggle('done', recoveryCodesConfirmed);
      refreshDoneState();
    });

    // ---- TOTP proof: chains request-bible-challenge and
    // verify-bible-challenge against the secret just installed. No
    // server-side change — see accompanying explanation. The
    // returned bibleSessionToken on success is deliberately
    // discarded; this step exists only to prove the authenticator
    // works, not to open a Bible session here. ----
    document.getElementById('cvnBibleVerifyCodeBtn')?.addEventListener('click', async () => {
      const statusEl = document.getElementById('cvnBibleVerifyStatus');
      const code = document.getElementById('cvnBibleVerifyCodeInput')?.value?.trim();
      if (!code) return;

      statusEl.textContent = 'Checking…';
      statusEl.className = 'cvn-be-verify-status';

      try {
        const challenge = await callRequestChallenge();
        if (!challenge.ok) {
          statusEl.textContent = mapErrorMessage(challenge.status, challenge.body);
          statusEl.className = 'cvn-be-verify-status fail';
          return;
        }
        liveAttemptId = challenge.body?.attemptId || null;
        if (!liveAttemptId) {
          statusEl.textContent = 'This could not be completed. Please try again.';
          statusEl.className = 'cvn-be-verify-status fail';
          return;
        }

        const verify = await callVerifyChallenge(liveAttemptId, code);
        if (!verify.ok || !verify.body?.bibleSessionToken) {
          statusEl.textContent = mapErrorMessage(verify.status, verify.body);
          statusEl.className = 'cvn-be-verify-status fail';
          return;
        }

        // Success. The issued session token is intentionally not
        // used or retained here — this step only proves the code
        // works. No reference to it is kept beyond this block.
        totpVerified = true;
        statusEl.textContent = 'Authenticator verified.';
        statusEl.className = 'cvn-be-verify-status ok';
        checklistTotp.textContent = '✓ Authenticator verified';
        checklistTotp.classList.add('done');
        refreshDoneState();
      } catch (error) {
        console.error('[Bible Enrollment] authenticator verification failed:', error);
        statusEl.textContent = 'This could not be completed. Please try again.';
        statusEl.className = 'cvn-be-verify-status fail';
      }
    });

    doneButton?.addEventListener('click', () => {
      if (!recoveryCodesConfirmed || !totpVerified) return;
      credentialsCurrentlyDisplayed = false;
      closeEnrollmentPanel();
    });
  }

  /* ============================================================
     FLOW CONTROL
     ============================================================ */

  function mapErrorMessage(status, body) {
    if (status === 401) return 'Your session has expired. Please sign in again.';
    if (status === 403 && body?.error === 'forbidden') return 'This account is not authorized for Bible administration.';
    if (status === 500) return 'A server configuration issue is preventing this. Contact the system administrator.';
    if (body?.error === 'locked_out') return 'Too many attempts. Please wait before trying again.';
    if (body?.error === 'attempt_already_in_progress') return 'A verification attempt is already in progress. Please wait a moment and try again.';
    if (body?.error === 'invalid_code') return 'That code was not recognized. Please try again.';
    return 'This could not be completed. Please try again.';
  }

  async function attemptEnroll(bibleSessionToken) {
    renderLoading('Contacting the secure enrollment service…');
    let result;
    try {
      result = await callEnroll(bibleSessionToken);
    } catch (error) {
      console.error('[Bible Enrollment] enroll call failed:', error);
      renderError('This could not be completed. Please try again.');
      return;
    }

    if (result.ok) {
      renderSuccess(result.body);
      return;
    }

    if (result.status === 403 && result.body?.error === 'bible_session_required') {
      renderChallengeForm(runChallengeFlow);
      return;
    }

    renderError(mapErrorMessage(result.status, result.body));
  }

  async function runChallengeFlow(code) {
    renderLoading('Requesting a verification attempt…');
    let challengeResult;
    try {
      challengeResult = await callRequestChallenge();
    } catch (error) {
      console.error('[Bible Enrollment] request-bible-challenge failed:', error);
      renderError('This could not be completed. Please try again.');
      return;
    }

    if (!challengeResult.ok) {
      renderError(mapErrorMessage(challengeResult.status, challengeResult.body));
      return;
    }

    const attemptId = challengeResult.body?.attemptId;
    if (!attemptId) {
      renderError('This could not be completed. Please try again.');
      return;
    }

    renderLoading('Verifying…');
    let verifyResult;
    try {
      verifyResult = await callVerifyChallenge(attemptId, code);
    } catch (error) {
      console.error('[Bible Enrollment] verify-bible-challenge failed:', error);
      renderError('This could not be completed. Please try again.');
      return;
    }

    if (!verifyResult.ok) {
      renderError(mapErrorMessage(verifyResult.status, verifyResult.body));
      return;
    }

    const bibleSessionToken = verifyResult.body?.bibleSessionToken;
    if (!bibleSessionToken) {
      renderError('This could not be completed. Please try again.');
      return;
    }

    // bibleSessionToken lives only in this local variable, passed
    // straight into the retry below. It is never stored anywhere.
    await attemptEnroll(bibleSessionToken);
  }

  /* ============================================================
     OVERLAY LIFECYCLE
     ============================================================ */

  function installOverlay() {
    if (overlayInstalled) return;
    overlayInstalled = true;
    installStyles();

    const overlay = document.createElement('div');
    overlay.id = 'cvnBibleEnrollOverlay';
    overlay.innerHTML = `
      <div class="cvn-be-shell">
        <div class="cvn-be-header">
          <div class="cvn-be-eyebrow">Corevian Bible — Security</div>
          <h2 class="cvn-be-title">Authenticator Setup</h2>
        </div>
        <div class="cvn-be-body" id="cvnBibleEnrollPanel"></div>
      </div>
    `;
    document.body.appendChild(overlay);

    overlay.addEventListener('click', (event) => {
      if (event.target !== overlay) return;
      // While the secret/recovery codes are on screen, a backdrop
      // click must NOT close the panel — this was the one remaining
      // path that could destroy the only displayed copy before it
      // was confirmed saved and verified.
      if (credentialsCurrentlyDisplayed) return;
      closeEnrollmentPanel();
    });
  }

  function closeEnrollmentPanel() {
    const overlay = document.getElementById('cvnBibleEnrollOverlay');
    if (overlay) overlay.classList.remove('cvn-open');
    // Removes the rendered credential material from the DOM and
    // releases this file's own references to it, so nothing is
    // visibly displayed or reachable through this module after the
    // panel closes. This is not a guarantee that the underlying
    // string values have been erased from process memory — the
    // JavaScript engine's garbage collector decides that on its own
    // schedule, and this code has no way to force or verify it.
    const panel = panelEl();
    if (panel) panel.innerHTML = '';
    document.body.style.overflow = '';
  }

  async function openBibleEnrollment() {
    if (isDemoMode()) {
      console.info('[Bible Enrollment] unavailable while Executive Demo is active.');
      return;
    }

    let session;
    try {
      session = await getAuthSession();
    } catch (error) {
      console.error('[Bible Enrollment] session read failed:', error);
      return;
    }

    if (!isAuthorizedCEO(session)) {
      console.info('[Bible Enrollment] not available for this account.');
      return;
    }

    installOverlay();
    const overlay = document.getElementById('cvnBibleEnrollOverlay');
    overlay.classList.add('cvn-open');
    document.body.style.overflow = 'hidden';

    await attemptEnroll(null);
  }

  window.openCorevianBibleEnrollment = openBibleEnrollment;

})();